import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import { useParams } from 'react-router';
import { Link } from '@repo/poly-hooks/components';
import { FormattedMoney, formatMoney, MONEY_TOKENS, Disclaimer } from '@repo/design-system-kit';
import { IconArrowLeftOutlined, IconLockOutlined } from '@repo/monochrome-icons';
import { useSegment } from '@repo/poly-hooks/segment';
import { LocaleSwitcher } from 'receivable-invoices-js/react/components/shared/locale-switcher';
import { CopyField } from 'receivable-invoices-js/react/components/shared/copy-field';
import { Referral } from 'receivable-invoices-js/react/components/invoices/referral';
import { QontoHeader } from 'receivable-invoices-js/react/components/shared/qonto-header';
import { useMedia } from 'receivable-invoices-js/react/hooks/use-media';
import { useRouteQuery } from './use-route-query';
import styles from './layout.module.css';

export function InvoicePayRoute(): ReactNode {
  const { id } = useParams<{ id: string }>();

  const { formatMessage, locale } = useIntl();
  const segment = useSegment();
  const { isDesktop, isMobile } = useMedia();

  // @ts-expect-error -- id is defined as this route is intercepting path with id
  const { invoice, referralUrl } = useRouteQuery(id);

  useEffect(() => {
    segment.track('public-page-payment_loaded');
  }, [segment]);

  if (!invoice) {
    return null;
  }

  const currency = invoice.currency || 'EUR';
  const showIntermediaryBic = invoice.currency !== 'EUR';
  const paymentMethod = invoice.paymentMethods[0];

  return (
    <div className={styles.content}>
      <header className={cx('mb-48', styles.header)}>
        <QontoHeader className={styles['qonto-header']} />
        <LocaleSwitcher />
      </header>

      <div className={styles.middle}>
        <div className={styles.left}>
          <main>
            <Link
              className={cx('mb-32', 'btn', 'btn--tertiary', 'body-1', styles['back-link'])}
              to={`/invoices/${id}`}
            >
              <IconArrowLeftOutlined aria-hidden="true" className="mr-8" />
              <FormattedMessage id="invoice.pay.go-back" />
            </Link>

            <h1 className="title-1" data-test-amount-title>
              <FormattedMoney
                amount={invoice.amountDue.value}
                currency={currency}
                token={MONEY_TOKENS.ISO}
              />
              <span className={cx('caption', styles.caption)}>
                <FormattedMessage id="invoice.include-vat" />
              </span>
            </h1>

            <h2 className={cx(isDesktop ? 'mb-24' : 'mb-32', styles['title-2'])}>
              <FormattedMessage
                id="invoice.pay.to"
                values={{ companyName: invoice.organization.legalName }}
              />
            </h2>

            <h3 className={cx('mb-16', styles['title-3'])}>
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx -- missing in copy */}
              <FormattedMessage id="invoice.pay.transfer-details" />:
            </h3>

            <CopyField
              className="mb-16"
              label={formatMessage({ id: 'invoice.pay.total-amount.label' })}
              trackEvent="public-page-invoice_total-amount_copied"
              value={formatMoney({
                amount: invoice.amountDue.value,
                currency,
                locale,
              })}
            />

            <CopyField
              className="mb-8"
              data-test-reference=""
              label={formatMessage({ id: 'invoice.pay.reference.label' })}
              trackEvent="public-page-invoice_reference_copied"
              value={invoice.reference}
            />

            {isMobile ? (
              <Disclaimer.Block className={cx('mb-16', styles.disclaimer)} level="info">
                <FormattedMessage id="invoice.pay.reference.disclaimer" />
              </Disclaimer.Block>
            ) : (
              <Disclaimer.Inline className={cx('mb-16', styles.disclaimer)} level="info">
                <FormattedMessage id="invoice.pay.reference.disclaimer" />
              </Disclaimer.Inline>
            )}

            <hr className={cx('mb-16', styles.divider)} />

            {paymentMethod ? (
              <>
                <CopyField
                  className="mb-16"
                  data-test-beneficiary=""
                  label={formatMessage({ id: 'invoice.pay.beneficiary.label' })}
                  trackEvent="public-page-invoice_beneficiary_copied"
                  value={paymentMethod.beneficiaryName}
                />

                <CopyField
                  className={paymentMethod.bic ? 'mb-16' : 'mb-32'}
                  data-test-iban=""
                  label={formatMessage({ id: 'invoice.pay.iban.label' })}
                  trackEvent="public-page-invoice_iban_copied"
                  value={paymentMethod.iban}
                />

                {paymentMethod.bic ? (
                  <CopyField
                    className={showIntermediaryBic ? 'mb-16' : 'mb-32'}
                    data-test-bic=""
                    label={formatMessage({ id: 'invoice.pay.bic.label' })}
                    trackEvent="public-page-invoice_bic_copied"
                    value={paymentMethod.bic}
                  />
                ) : null}
              </>
            ) : null}

            {showIntermediaryBic ? (
              <CopyField
                className="mb-32"
                data-test-intermediary-bic=""
                label={formatMessage({ id: 'invoice.pay.intermediary-bic.label' })}
                value={formatMessage({ id: 'invoice.pay.intermediary-bic.value' })}
              />
            ) : null}
          </main>

          <footer className={cx('caption', 'mb-32', styles.footer)}>
            <span>
              <IconLockOutlined aria-hidden="true" className={cx('mr-8', styles.lock)} />
              <FormattedMessage id="invoice.secure" />
            </span>
          </footer>
        </div>

        {isDesktop ? (
          <div className={cx('mb-32', styles['figure-container'])}>
            <figure className={styles.figure} data-test-hero-image>
              <img alt="" className={cx('mb-32', styles['hero-img'])} src="/images/hero.png" />
              {referralUrl ? (
                <Referral
                  companyName={invoice.organization.legalName || ''}
                  referralUrl={referralUrl}
                />
              ) : null}
            </figure>
          </div>
        ) : null}
      </div>
    </div>
  );
}
